import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';


@Component({
  selector: 'black-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.css'],
  standalone: true,
  imports: [ImageCropperComponent],
})
export class ImageUploadComponent {
  @Input() uploadUrl = '/api/upload/image-cropper';
  @Output() imageUploaded = new EventEmitter<{ file: File; cropData?: any }>();

  imageChangedEvent: Event | null = null;
  croppedImage: string | null = '';
  croppedBlob: Blob | null = null;
  cropData: ImageCroppedEvent | null = null;
  isCropping = false;
  isUploading = false;
  uploadError = '';
  uploadedImageUrl: string | null = null;

  handleFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }
    this.isCropping = true;
    this.imageChangedEvent = event;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64 || '';
    this.croppedBlob = event.blob || null;
    this.cropData = event;
  }

  imageLoaded() {
    // show cropper
  }

  cropperReady() {
    // cropper ready
  }

  loadImageFailed() {
    this.uploadError = 'Failed to load image.';
  }

  uploadCroppedImage() {
    if (!this.croppedImage && !this.croppedBlob) {
      this.uploadError = 'No cropped image available.';
      return;
    }
    this.isUploading = true;
    this.uploadError = '';
    fetch(this.uploadUrl, {
      method: 'POST',
      body: this.createFormData(),
    })
      .then(async (response) => {
        this.isUploading = false;
        if (!response.ok) {
          throw new Error('Upload failed');
        }
        const result = await response.json();
        // Try to get the public URL from the backend response
        this.uploadedImageUrl = result.url || result.location || null;
        const file = this.getCroppedFile();
        if (file) {
          this.imageUploaded.emit({ file, cropData: this.cropData });
        }
        this.reset(false); // Don't clear uploadedImageUrl
      })
      .catch((err) => {
        this.isUploading = false;
        this.isCropping = false;
        this.uploadError = err.message || 'Upload failed.';
      });
  }

  createFormData(): FormData {
    const formData = new FormData();
    const file = this.getCroppedFile();
    if (file) {
      formData.append('file', file);
    }
    if (this.cropData) {
      formData.append('cropData', JSON.stringify(this.cropData));
    }
    return formData;
  }

  getCroppedFile(): File | null {
    if (this.croppedImage) {
      return this.dataURLtoFile(this.croppedImage, 'cropped.png');
    } else if (this.croppedBlob) {
      return new File([this.croppedBlob], 'cropped.png', { type: this.croppedBlob.type || 'image/png' });
    }
    return null;
  }

  dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : '';
    const bstr = atob(arr[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    return new File([u8arr], filename, { type: mime });
  }

  reset(clearUploadedUrl = true) {
    this.imageChangedEvent = null;
    this.croppedImage = '';
    this.croppedBlob = null;
    this.cropData = null;
    this.isCropping = false;
    this.isUploading = false;
    this.uploadError = '';
    if (clearUploadedUrl) {
      this.uploadedImageUrl = null;
    }
  }

  removeImage() {
    this.uploadedImageUrl = null;
    this.uploadError = '';
    this.croppedImage = '';
    this.croppedBlob = null;
    this.cropData = null;
    this.isCropping = false;
    this.isUploading = false;
  }
} 